/**
 * Where a capsule's content is kept, in words a user can act on — the same
 * durable / ephemeral split the Send screen shows right after sending.
 *
 * The backend records `storage_backend` (which store took the blob) and
 * `storage_ref` (that store's object path, `<bucket>/<sha256>.bin`). Neither
 * is shown as is: the store's name and the bucket path are infrastructure, not
 * something a user can use. What a user needs is whether the content should
 * survive a server update, and the stored blob's SHA-256.
 *
 * `storage_backend` is what the record declares, not a check of the store:
 * the wording says "recorded as", and the detail view says so when the content
 * could not be read (`contentUnavailable`).
 *
 *   'supabase'           durable    (synapse-api collectCapsuleRoutes)
 *   'local', 'local-fs'  ephemeral  (the server's own disk, cleared on redeploy)
 *   anything else        unknown    (false = nothing stored, e.g. a draft;
 *                                    stores this app does not know: s3, ipfs…)
 *
 * Pure module so it can be unit-tested with `node --test`.
 */

export type StorageKind = 'durable' | 'ephemeral' | 'unknown';

export interface CapsuleStorage {
  kind: StorageKind;
  /** Short label for fields and pills: "Durable" | "Ephemeral" | "Unknown" */
  label: string;
  /** One-line explanation for the detail view */
  description: string;
  /** The content could not be read from this server when it was last tried. */
  contentUnavailable: boolean;
}

const DURABLE_BACKENDS = new Set(['supabase']);
const EPHEMERAL_BACKENDS = new Set(['local', 'local-fs']);

const LABELS: Record<StorageKind, string> = { durable: 'Durable', ephemeral: 'Ephemeral', unknown: 'Unknown' };

const DESCRIPTIONS: Record<StorageKind, string> = {
  durable: 'Recorded as kept in durable storage, which survives server restarts and updates.',
  ephemeral:
    'Recorded as kept only in temporary server storage, which is cleared when the server is updated. Send the capsule again to keep its content durably.',
  unknown:
    'The record does not say where the content is kept (it may not have been stored at all), or names a store this app does not know. View contents shows whether it can be read.',
};

const RECORDED_AS: Record<StorageKind, string> = {
  durable: 'Recorded as kept in durable storage',
  ephemeral: 'Recorded as kept in temporary server storage',
  unknown: 'The record does not name a store this app knows',
};

const UNAVAILABLE_NOTE =
  'but this server could not find the content when you opened it (View contents). The record is intact; send the capsule again to store its content.';

export function storageKindOf(storageBackend: unknown): StorageKind {
  const v = typeof storageBackend === 'string' ? storageBackend.trim().toLowerCase() : '';
  if (DURABLE_BACKENDS.has(v)) return 'durable';
  if (EPHEMERAL_BACKENDS.has(v)) return 'ephemeral';
  return 'unknown';
}

export function capsuleStorage(
  storageBackend: unknown,
  opts: { contentUnavailable?: boolean } = {},
): CapsuleStorage {
  const kind = storageKindOf(storageBackend);
  const contentUnavailable = opts.contentUnavailable === true;
  return {
    kind,
    label: LABELS[kind],
    description: contentUnavailable ? `${RECORDED_AS[kind]}, ${UNAVAILABLE_NOTE}` : DESCRIPTIONS[kind],
    contentUnavailable,
  };
}

const BLOB_REF_RE = /(?:^|\/)([a-f0-9]{64})\.bin$/i;

/**
 * The SHA-256 of the stored blob, from `storage_ref` (`<bucket>/<sha256>.bin`);
 * null when the reference has another shape or is not a string. Only the hash
 * is returned — never the bucket or path.
 */
export function storedBlobSha256(storageRef: unknown): string | null {
  if (typeof storageRef !== 'string') return null;
  const m = BLOB_REF_RE.exec(storageRef.trim());
  return m ? m[1].toLowerCase() : null;
}
